import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ARRANGE_GAP, type ArrangeMode } from '@os/engine';
import { Button, Panel, Slider } from '../../ui';

const MODES: ArrangeMode[] = ['grid', 'row', 'column', 'masonry'];

type ArrangeController = {
  previewArrange(mode: ArrangeMode, gap: number): void;
  applyArrange(mode: ArrangeMode, gap: number): void;
  cancelArrange(): void;
};

/**
 * "Arrange" settings with a live preview (nothing is written until Apply: one undo step). A `Panel` (P16-2): its close
 * button, Escape and a press outside cancel the preview.
 */
export function ArrangePanel({ controller, onClose }: { controller: ArrangeController; onClose: () => void }) {
  const { t } = useTranslation();
  const [mode, setMode] = useState<ArrangeMode>('grid');
  const [gap, setGap] = useState(ARRANGE_GAP);
  const apply = useRef<HTMLButtonElement>(null);
  useEffect(() => controller.previewArrange(mode, gap), [controller, mode, gap]);
  const cancel = () => {
    controller.cancelArrange();
    onClose();
  };
  return (
    <Panel title={t('arrange.title')} closeLabel={t('common.close')} onClose={cancel} initialFocus={apply} finalFocus={false} className="w-64">
      <div className="mb-3 grid grid-cols-2 gap-1">
        {MODES.map((m) => (
          <Button
            key={m}
            size="sm"
            aria-pressed={mode === m}
            onClick={() => setMode(m)}
            className="aria-pressed:border-accent aria-pressed:bg-accent aria-pressed:text-accent-fg"
          >
            {t(`arrange.${m}`)}
          </Button>
        ))}
      </div>
      <Slider label={t('arrange.gap')} value={gap} min={0} max={200} onValueChange={setGap} className="mb-3" />
      <div className="flex justify-end gap-2">
        <Button variant="ghost" size="sm" onClick={cancel}>
          {t('arrange.cancel')}
        </Button>
        <Button
          ref={apply}
          variant="primary"
          size="sm"
          onClick={() => {
            controller.applyArrange(mode, gap);
            onClose();
          }}
        >
          {t('arrange.apply')}
        </Button>
      </div>
    </Panel>
  );
}
